import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { JwtService } from './jwt.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private jwtService: JwtService
  ) {}

  private formatErrors(error: any) {
    return throwError(() => error.error || error);
  }

  /**
   * Resolves endpoint URLs:
   * - Water, Sleep, Period endpoints -> https://dev.niwi.ai/ + 'api/'
   * - All other endpoints -> https://dev-hub.niwi.ai/
   */
  private getUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    // Detect water, sleep, and period tracking endpoints
    const isHealthTracker = 
      cleanPath.includes('log_water') ||
      cleanPath.includes('get_water_history') ||
      cleanPath.includes('log_sleep') ||
      cleanPath.includes('get_sleep_history') ||
      cleanPath.includes('get_tracker_data') ||
      cleanPath.includes('log_period') ||
      cleanPath.includes('log_symptoms') ||
      cleanPath.includes('AIClientDiet');

    if (isHealthTracker) {
      const baseUrl = environment.api.dev_url.endsWith('/')
        ? environment.api.dev_url
        : `${environment.api.dev_url}/`;
      
      // Ensures /api is prepended to the path
      const endpoint = cleanPath.startsWith('api/') ? cleanPath : `api/${cleanPath}`;
      return `${baseUrl}${endpoint}`;
    }

    // Default to dev-hub.niwi.ai for all other calls
    const defaultBase = environment.api.url.endsWith('/')
      ? environment.api.url
      : `${environment.api.url}/`;

    return `${defaultBase}${cleanPath}`;
  }

  get(path: string, params: HttpParams = new HttpParams()): Observable<any> {
    return this.http.get(this.getUrl(path), { params })
      .pipe(catchError(this.formatErrors));
  }

  put(path: string, body: Object = {}): Observable<any> {
    return this.http.put(
      this.getUrl(path),
      JSON.stringify(body)
    ).pipe(catchError(this.formatErrors));
  }

  post(path: string, body: Object = {}): Observable<any> {
    return this.http.post(
      this.getUrl(path),
      JSON.stringify(body)
    ).pipe(catchError(this.formatErrors));
  }

  delete(path: string): Observable<any> {
    return this.http.delete(
      this.getUrl(path)
    ).pipe(catchError(this.formatErrors));
  }

  postState(path: string, body: Object = {}): Observable<any> {
    return this.http.post(
      this.getUrl(path),
      JSON.stringify(body)
    ).pipe(catchError(this.formatErrors));
  }

  uploadDocuments(url: string, formData: FormData): Observable<any> {
    return this.http.post(this.getUrl(url), formData, {
      headers: new HttpHeaders({
        Accept: '*/*'
      })
    }).pipe(catchError(this.formatErrors));
  }
}